/**
 * Couche réseau du module Partage — UNE traduction des erreurs, pas quatre.
 *
 * Le patron « un `api.ts` par module » est celui du dépôt
 * (`organisations/api.ts`, `organisations/api-vevidental.ts`). Il n'y a
 * volontairement PAS de revalidation zod ici, contrairement à
 * `organisations/api.ts` : le module Partage a tranché l'inverse à sa création
 * (`routes/Index.tsx` lisait déjà `Cas[]` sans schéma), et introduire un
 * troisième style au milieu d'un lot serait pire que l'un ou l'autre.
 *
 * §17.5 — la raison d'être du fichier : trois écrans vont appeler le BFF, et
 * trois copies du même « 403 → ton rôle ne permet pas » dérivent toujours.
 */

/**
 * Traduit une réponse en échec en une phrase adressée à un humain.
 *
 * Le message du SERVEUR prend le pas sur le code HTTP quand il existe : les
 * routes du module rendent `400 {error: "..."}` pour une règle métier, et
 * c'est la seule phrase qui dit quoi corriger.
 */
interface CorpsErreur {
  error?: string;
  /** Les `issues` de zod, telles que les routes les renvoient sur un 400. */
  details?: { path?: unknown[]; message?: string }[];
}

/**
 * Le premier champ fautif, en clair — ou `null` s'il n'y en a pas.
 *
 * On ne montre PAS la liste brute de zod à un humain : un chemin
 * `["instructions","remarques"]` devient « remarques ». Le message de zod est
 * en anglais et parle de « String must contain at most… » — on garde donc le
 * nom du champ, qui suffit à savoir où regarder, sans traduire une phrase que
 * personne n'a écrite pour être lue.
 */
function champFautif(body: CorpsErreur | null): string | null {
  const premier = body?.details?.[0]?.path;
  if (premier === undefined || premier.length === 0) return null;
  const dernier = premier[premier.length - 1];
  return typeof dernier === "string" ? dernier : null;
}

/*
 * UN 403 N'A PAS UNE SEULE CAUSE, et le front n'a pas à en inventer une.
 *
 * Ce code sortait ici avec « Ton rôle ne permet pas cette action (D8) ». Or la
 * garde du serveur refuse pour DEUX raisons distinctes, et elle les nomme :
 * le module éteint par son drapeau, ou le rôle sans accès. En sortant avant de
 * lire la réponse, le front jetait la seule information utile.
 *
 * Vu en production le 16/08 : l'écran annonçait un problème de droits alors que
 * le drapeau serveur était simplement éteint. Dove a cherché du côté des rôles.
 * Un message qui fait chercher au mauvais endroit coûte plus cher qu'un message
 * générique.
 *
 * Le 401 garde son message maison : « non authentifié » ne dit rien à un humain,
 * alors que « recharger la page » est actionnable.
 */
/**
 * LE REFUS, AVEC DE QUOI ÊTRE RECONNU PAR LA MACHINE.
 *
 * ⚠ `message` RESTE LA PHRASE POUR L'HUMAIN — tous les appelants existants la
 * lisent, et aucun ne change. Ce qui s'ajoute est ce que l'`Error` nu jetait :
 * le statut, et le `code` stable que le serveur pose sur les refus qu'un écran
 * doit savoir distinguer. Sans lui, la seule prise serait le texte français —
 * qui se reformule un jour, sans rien rendre rouge.
 */
export class ErreurHttp extends Error {
  constructor(
    message: string,
    readonly statut: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ErreurHttp";
  }
}

async function enErreur(res: Response): Promise<Error> {
  if (res.status === 401) {
    return new ErreurHttp("Session expirée — recharger la page pour se reconnecter.", 401);
  }
  const body = (await res.json().catch(() => null)) as (CorpsErreur & { code?: string }) | null;
  const message = body?.error ?? `Le serveur a répondu ${String(res.status)}.`;
  const champ = champFautif(body);
  return new ErreurHttp(
    champ === null ? message : `${message} — champ « ${champ} »`,
    res.status,
    body?.code,
  );
}

/**
 * `entetes` est arrivé avec É8 : le jeton du lien public voyage dans un
 * en-tête, jamais dans le chemin — une URL est journalisée, un en-tête ne
 * l'est pas (audit du 29/07).
 */
export async function lireJson<T>(url: string, entetes?: Record<string, string>): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...(entetes ? { headers: entetes } : {}) });
  if (!res.ok) throw await enErreur(res);
  return (await res.json()) as T;
}

/** Écriture qui rend une ressource — `POST /lieux` (201), `POST /cas` (201). */
export async function envoyerJson<T>(url: string, corps: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(corps),
  });
  if (!res.ok) throw await enErreur(res);
  return (await res.json()) as T;
}

/**
 * Écriture qui ne rend RIEN — `PATCH /cas/:casId/etapes/:ordre` répond 204 sans
 * corps (`enRegleMetier`). Lire le corps d'un 204 lèverait APRÈS une écriture
 * réussie : l'écran afficherait une erreur pour un succès. Deux fonctions
 * plutôt qu'un `as T` sur `undefined` — le typage dit alors la vérité.
 */
export async function envoyerSansReponse(
  url: string,
  methode: "POST" | "PATCH" | "PUT" | "DELETE",
  corps?: unknown,
): Promise<void> {
  /*
   * DELETE est arrivé avec É8 (révoquer un lien public), PUT avec É9 (poser
   * une dérogation : deux fois le même appel doit donner le même résultat,
   * jamais deux lignes). Un DELETE ne porte pas de corps : lui en fabriquer un vide (`JSON.stringify(undefined)` rend
   * la chaîne « undefined ») ferait échouer le parseur JSON de Fastify AVANT
   * la route, sur un 400 qui n'accuserait rien de juste.
   */
  const res = await fetch(url, {
    method: methode,
    credentials: "include",
    ...(corps === undefined
      ? {}
      : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(corps) }),
  });
  if (!res.ok) throw await enErreur(res);
}
